import React, { useState } from 'react';
import { api } from '../utils/api';
import { showToast } from '../utils/toast';
import { Play, History, Info, Activity, Target, RefreshCw, Award, Dumbbell, ChevronLeft, Wrench, Share2, Star, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react';
import { getCoachMessage } from '../utils/dynamicCoach';

const AVOID_OPTIONS = [
    { key: 'lower_back', label: 'Onderrug' },
    { key: 'shoulders', label: 'Schouders' },
    { key: 'knees_legs', label: 'Knieën / Benen' },
    { key: 'chest', label: 'Borst' },
    { key: 'upper_back', label: 'Bovenrug' }
];

export default function Dashboard({
    user, currentWod, history, trainingDays, getWod, isRecovery,
    liveExercises, groups, exerciseDatabase, LOCAL_EXERCISES,
    requestWakeLock, setupTimerForWod, setView, btnClass, setSwapModal, handleShareFlex
}) {
    const safeHistory = Array.isArray(history) ? history : [];

    const [isSavingFav, setIsSavingFav] = useState(false);
    const [showReadinessDrawer, setShowReadinessDrawer] = useState(false);
    const [avoidedMuscles, setAvoidedMuscles] = useState([]);

    const toggleAvoidMuscle = (key) => {
        setAvoidedMuscles(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    };

    const handleSaveFavorite = async () => {
        if (!currentWod) return;
        setIsSavingFav(true);
        try {
            await api.fetch('/workouts/favorites', { method: 'POST', body: JSON.stringify(currentWod) }, localStorage.getItem('token'));
            showToast('Opgeslagen in favorieten! ⭐', 'success');
        } catch (error) {
            showToast('Fout bij opslaan favoriet', 'error');
        } finally {
            setTimeout(() => setIsSavingFav(false), 500);
        }
    };
    const nowDash = new Date();
    const dayOfWeekDash = nowDash.getDay() === 0 ? 6 : nowDash.getDay() - 1;
    const startOfWeekDash = new Date(nowDash);
    startOfWeekDash.setDate(nowDash.getDate() - dayOfWeekDash);
    startOfWeekDash.setHours(0, 0, 0, 0);

    const countThisWeekDash = new Set(
        safeHistory
            .filter(h => new Date(h.date) >= startOfWeekDash)
            .map(h => new Date(h.date).toDateString())
    ).size;

    const progressDash = Math.min(100, (countThisWeekDash / trainingDays) * 100);

    const msPerDay = 1000 * 60 * 60 * 24;
    const last28DaysWorkouts = safeHistory.filter(h => ((nowDash - new Date(h.date)) / msPerDay) <= 28);
    const hadRecentDeload = last28DaysWorkouts.some(h => {
        const dAgo = (nowDash - new Date(h.date)) / msPerDay;
        const txt = ((h.type || '') + ' ' + (h.description || '')).toLowerCase();
        return dAgo <= 14 && txt.includes('deload');
    });
    const weekBuckets = [0, 0, 0, 0];
    last28DaysWorkouts.forEach(h => {
        const dAgo = Math.floor((nowDash - new Date(h.date)) / msPerDay);
        const bucket = Math.min(3, Math.floor(dAgo / 7));
        if (bucket >= 0 && bucket <= 3) weekBuckets[bucket]++;
    });
    const activeWeeksCount = weekBuckets.filter(c => c >= 2).length;
    const isDeloadRecommended = !hadRecentDeload && activeWeeksCount >= 4 && last28DaysWorkouts.length >= 12;

    const lastWorkoutDash = safeHistory.length > 0 ? safeHistory[0] : null;
    let lastWorkoutTextDash = "Geen sessies.";
    let trainedToday = false;
    let recentLoadLast7Days = false;

    if (lastWorkoutDash) {
        const lastWorkoutDate = new Date(lastWorkoutDash.date);
        trainedToday = lastWorkoutDate.toDateString() === nowDash.toDateString();
        const daysAgo = Math.floor((nowDash - lastWorkoutDate) / (1000 * 60 * 60 * 24));
        recentLoadLast7Days = daysAgo <= 7;
        const dayText = daysAgo === 0 ? 'Vandaag' : daysAgo === 1 ? 'Gisteren' : `${daysAgo}d geleden`;
        lastWorkoutTextDash = `${dayText}: ${lastWorkoutDash.type}`;
    }

    const userType = user?.training_type?.toLowerCase() || '';
    const isFitnessOrShredding = userType.includes('fitness') || userType.includes('shredding') || userType.includes('mix') || userType.includes('commercial gym') || userType.includes('spartan');

    let coachText = getCoachMessage(user, safeHistory, trainingDays);

    if (trainedToday && !isFitnessOrShredding) {
        coachText = `Lekker bezig vandaag, ${user?.name?.split(' ')[0] || 'topper'}! Je hebt vandaag al getraind. Je kunt gerust nog een korte WOD of Cardio finisher meepakken, maar bewaak je grenzen (Max 1 uur totaal).`;
    }

    let pastPerformanceText = null;

    if (currentWod) {
        const pastWod = safeHistory.find(h =>
            h.type === currentWod.type ||
            (h.description && h.description.includes(currentWod.title))
        );

        if (pastWod) {
            const dateStr = new Date(pastWod.date).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
            const match = pastWod.description.match(/(Score:|Tijd:|Voltooid:)\s*(.*)/);
            const result = match ? match[2].trim() : 'Voltooid';
            pastPerformanceText = `Laatste keer (${dateStr}): ${result}`;
        }
    }

    const isShreddingUser = user?.training_type === 'Shredding Programma' || user?.training_type === 'Shredding';
    let displayFormat = currentWod?.format;
    let displayTitle = currentWod?.title;

    if (isShreddingUser) {
        if (displayFormat === 'Krachttraining') displayFormat = 'SHREDDING';
        if (displayTitle && displayTitle.toUpperCase().includes('KRACHT')) {
            displayTitle = displayTitle.replace(/Krachttraining/ig, 'Shredding');
        }
    }

    const glassClass = "bg-zinc-900/90 backdrop-blur-xl border border-zinc-700 shadow-2xl rounded-2xl p-6 text-white";

    let currentExercisesDetails = [];

    if (currentWod) {
        if (currentWod.isFitness && liveExercises && liveExercises.length > 0) {
            liveExercises.forEach(ex => {
                const cleanName = ex.cleanName || ex.name || 'Oefening';
                let tipDesc = ex.description || ex.tip || ex.desc;

                if (!tipDesc) {
                    const dbMatch = exerciseDatabase.find(e => (e.exercise_name || e.title || e.name || '').toLowerCase() === cleanName.toLowerCase());
                    if (dbMatch) {
                        tipDesc = dbMatch.tip || dbMatch.desc || dbMatch.description;
                    }
                }

                if (tipDesc && !currentExercisesDetails.find(d => d.name === cleanName)) {
                    currentExercisesDetails.push({ name: cleanName, desc: tipDesc });
                }
            });
        } else {
            const textToScan = (currentWod.title + " " + (currentWod.description || "")).toLowerCase();
            const specificTips = [
                { keywords: ["roeien", "roeier", "row"], name: "Techniek: Roeier", desc: "Focus op de afzet vanuit de benen (60%), dan de rug (30%) en als laatste de armen (10%). Trek de hendel naar je onderste ribben." },
                { keywords: ["loopband", "hardlopen", "rennen", "run", "sprints"], name: "Techniek: Hardlopen", desc: "Land op je middenvoet en houd een lichte voorwaartse leun vanuit je enkels. Ontspan je schouders." },
                { keywords: ["fiets", "fietsen", "bike", "ride"], name: "Techniek: Fietsen", desc: "Stel het zadel in op heuphoogte. Houd een constant toerental aan en focus op een soepele, ronde trapbeweging." },
                { keywords: ["assault", "airbike", "echo"], name: "Techniek: Air Bike", desc: "Gebruik je hele lichaam. Duw en trek krachtig met je armen om je benen te ontlasten tijdens zware intervallen." },
                { keywords: ["ski", "skierg"], name: "Techniek: SkiErg", desc: "Start met je armen hoog. Trek explosief naar down door je heupen naar achteren te gooien alsof je een zware crunch maakt." }
            ];
            const generalTips = [
                { name: "Coach Tip: Ademhaling", desc: "Probeer door je neus in te ademen en door je mond uit om je hartslag onder controle te houden, zeker tijdens actieve rust." },
                { name: "Coach Tip: Pacing", desc: "Begin de eerste rondes gecontroleerd. Het is veel makkelijker om aan het eind te versnellen dan te herstellen van een te snelle start." },
                { name: "Coach Tip: Postuur", desc: "Ontspan je schouders en nek, ongeacht het apparaat. Spanning in je bovenlichaam kost onnodige zuurstof." },
                { name: "Coach Tip: Mentale Focus", desc: "Kijk niet continu naar de timer. Focus op de beweging van het moment en deel de tijd op in kleine, behapbare blokjes." }
            ];

            specificTips.forEach(tip => {
                if (tip.keywords.some(kw => textToScan.includes(kw))) {
                    if (!currentExercisesDetails.find(d => d.name === tip.name)) {
                        currentExercisesDetails.push({ name: tip.name, desc: tip.desc });
                    }
                }
            });

            const shuffledGeneral = [...generalTips].sort(() => 0.5 - Math.random());
            let tipIndex = 0;
            while (currentExercisesDetails.length < 3 && tipIndex < shuffledGeneral.length) {
                currentExercisesDetails.push(shuffledGeneral[tipIndex]);
                tipIndex++;
            }
        }
    }

    let buttonText = "GENEREER WORKOUT";
    if (userType === 'fitness') buttonText = 'GENEREER TRAINING';
    else if (userType === 'crossfit' || userType === 'mix') buttonText = 'GENEREER WOD';
    else if (userType.includes('shredding')) buttonText = 'START SHREDDING';
    else if (userType.includes('spartan')) buttonText = 'START SPARTAN300';
    else if (userType === 'cardio') buttonText = 'START CARDIO';

    const readinessTriggerLabel = recentLoadLast7Days
        ? "Spierpijn of stijfheid vandaag?"
        : "Blessure of gevoelig gewricht?";

    return (
        <div className={glassClass}>
            <style>{`
                @keyframes firePulse {
                    0% { box-shadow: 0 0 15px rgba(234, 88, 12, 0.4); }
                    50% { box-shadow: 0 0 35px rgba(234, 88, 12, 0.8); }
                    100% { box-shadow: 0 0 15px rgba(234, 88, 12, 0.4); }
                }
                .glow-fire { animation: firePulse 3s infinite; }

                @keyframes icePulse {
                    0% { box-shadow: 0 0 15px rgba(6, 182, 212, 0.4); }
                    50% { box-shadow: 0 0 35px rgba(6, 182, 212, 0.8); }
                    100% { box-shadow: 0 0 15px rgba(6, 182, 212, 0.4); }
                }
                .glow-ice { animation: icePulse 3s infinite; animation-delay: 1.5s; }
                @keyframes emeraldPulse {
                    0% { box-shadow: 0 0 15px rgba(16, 185, 129, 0.4); }
                    50% { box-shadow: 0 0 35px rgba(16, 185, 129, 0.8); }
                    100% { box-shadow: 0 0 15px rgba(16, 185, 129, 0.4); }
                }
                .glow-emerald { animation: emeraldPulse 3s infinite; animation-delay: 0.75s; }
            `}</style>

            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4 border-b border-zinc-700 pb-6">
                <div>
                    <h2 className="text-3xl sm:text-4xl font-normal mb-1 text-white">Welkom, <span className="font-bold">{user.name}</span>!</h2>
                    <p className="text-gray-500 text-sm uppercase tracking-widest">Niveau: <span className="text-orange-500 font-bold">{user.level}</span> - Focus: {user.training_type}</p>
                </div>
            </div>

            {isDeloadRecommended && !currentWod && (
                <div className="mb-6 bg-zinc-800/50 border border-emerald-500/50 rounded-2xl p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-lg">
                    <div className="flex items-start gap-3">
                        <ShieldAlert className="text-emerald-500 shrink-0 mt-0.5" size={24} />
                        <div>
                            <h4 className="text-emerald-500 font-black uppercase tracking-wider text-sm">Mesocyclus Voltooid (4 Weken Streak!) 🌿</h4>
                            <p className="text-gray-300 text-xs sm:text-sm mt-1">
                                Je hebt de afgelopen 4 weken al <strong>{last28DaysWorkouts.length} workouts</strong> gedraaid. De AI-coach adviseert een <strong>Deload-sessie (65% intensiteit)</strong> om je pezen en zenuwstelsel te laten supercompenseren.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={() => getWod('deload', false, avoidedMuscles)}
                        className="glow-emerald shrink-0 w-full sm:w-auto px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white font-black text-xs sm:text-sm uppercase tracking-widest rounded-xl transition-all"
                    >
                        🌿 Start Deload
                    </button>
                </div>
            )}

            {!currentWod ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                    <div className="flex flex-col gap-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="bg-zinc-800/50 p-4 rounded-2xl border border-orange-500/20 flex flex-col justify-center items-center text-center shadow-inner">
                                <h3 className="text-white text-xs font-normal uppercase tracking-widest mb-3 flex items-center gap-1"><Target size={14}/> Weekdoel</h3>
                                <div className="relative w-16 h-16 flex items-center justify-center">
                                    <svg className="absolute w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                                        <path className="text-zinc-700" strokeWidth="4" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                        <path className={`${progressDash >= 100 ? 'text-green-500' : 'text-orange-500'}`} strokeDasharray={`${progressDash}, 100`} strokeWidth="4" strokeLinecap="round" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    </svg>
                                    <span className="font-black text-xl z-10 text-white">{countThisWeekDash} <span className="text-sm font-normal text-white">/{trainingDays}</span></span>
                                </div>
                            </div>

                            <div className="bg-zinc-800/50 p-4 rounded-2xl border border-orange-500/20 flex flex-col justify-center items-center text-center shadow-inner">
                                <h3 className="text-white text-xs font-normal uppercase tracking-widest mb-3 flex items-center gap-1"><History size={14}/> Laatste</h3>
                                <p className="font-normal text-sm text-white leading-snug">{lastWorkoutTextDash}</p>
                            </div>
                        </div>

                        <div className="bg-zinc-800/30 p-5 rounded-2xl border border-orange-500/20 flex-1 flex flex-col justify-center">
                            <h3 className="text-orange-500 text-xs font-bold uppercase tracking-widest mb-2 flex items-center gap-2"><Info size={14}/> Coach Update</h3>
                            <p className="text-gray-300 text-sm leading-relaxed font-normal">{coachText}</p>
                        </div>
                    </div>

                    <div className="bg-zinc-800/10 p-5 sm:p-8 rounded-2xl border border-zinc-700/50 flex flex-col items-center justify-center text-center relative overflow-hidden">
                        <h3 className="text-2xl font-black text-white mb-1 relative z-10">Tijd om te werken.</h3>
                        <p className="text-gray-400 font-normal text-xs sm:text-sm mb-4 relative z-10">Kies je focus voor vandaag.</p>

                        <div className="w-full flex flex-col gap-3.5 relative z-10">
                            {/* 📱 Mobile-Friendly 1-Klik Spierpijn / Blessure Lade (Light & Dark Mode compatible) */}
                            <div className="w-full bg-zinc-800/80 border border-zinc-700 rounded-xl overflow-hidden transition-all shadow-sm">
                                <button
                                    type="button"
                                    onClick={() => setShowReadinessDrawer(!showReadinessDrawer)}
                                    className="w-full px-3.5 py-2.5 flex items-center justify-between text-xs font-bold text-gray-300 hover:text-orange-500 transition-colors"
                                >
                                    <span className="flex items-center gap-2">
                                        <span>🛡️ {readinessTriggerLabel}</span>
                                        {avoidedMuscles.length > 0 && (
                                            <span className="bg-orange-500/20 text-orange-500 border border-orange-500/40 px-2 py-0.5 rounded-full text-[10px] font-black uppercase">
                                                {avoidedMuscles.length} ontzien
                                            </span>
                                        )}
                                    </span>
                                    {showReadinessDrawer ? <ChevronUp size={16} className="text-orange-500" /> : <ChevronDown size={16} className="text-gray-400" />}
                                </button>

                                {showReadinessDrawer && (
                                    <div className="px-3.5 pb-3.5 pt-2 border-t border-zinc-700 text-left">
                                        <p className="text-[11px] text-gray-400 mb-2">
                                            Tik aan wat de AI-engine vandaag moet ontzien of vervangen:
                                        </p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {AVOID_OPTIONS.map(opt => {
                                                const active = avoidedMuscles.includes(opt.key);
                                                return (
                                                    <button
                                                        key={opt.key}
                                                        type="button"
                                                        onClick={() => toggleAvoidMuscle(opt.key)}
                                                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all border ${
                                                            active
                                                                ? 'bg-orange-600 text-white border-orange-500 shadow-sm'
                                                                : 'bg-zinc-900/80 text-gray-300 border-zinc-700 hover:border-orange-500/50'
                                                        }`}
                                                    >
                                                        {active ? `✓ ${opt.label}` : opt.label}
                                                    </button>
                                                );
                                            })}
                                            {avoidedMuscles.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setAvoidedMuscles([])}
                                                    className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold text-gray-400 hover:text-orange-500 underline"
                                                >
                                                    Reset
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <button onClick={() => getWod('default', false, avoidedMuscles)} className="glow-fire w-full py-5 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white text-lg sm:text-xl font-black rounded-xl transition-all flex items-center justify-center gap-3 tracking-widest uppercase">
                                {trainedToday && !isFitnessOrShredding ? 'NOG EEN WOD' : buttonText} <Play size={20} fill="currentColor"/>
                            </button>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
                                <button onClick={() => getWod('cardio', false, avoidedMuscles)} className="glow-ice w-full py-4 bg-gradient-to-r from-cyan-600 to-blue-700 hover:from-cyan-500 hover:to-blue-600 text-white text-sm font-black rounded-xl transition-all flex items-center justify-center gap-2 tracking-widest uppercase shadow-md">
                                    START CARDIO <Activity size={18}/>
                                </button>

                                <button onClick={() => getWod('deload', false, avoidedMuscles)} className="glow-emerald w-full py-4 bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-500 hover:to-teal-600 text-white text-sm font-black rounded-xl transition-all flex items-center justify-center gap-2 tracking-widest uppercase shadow-md" title="Genereer een hersteltraining op 65% intensiteit">
                                    DELOAD SESSIE
                                </button>

                                <button onClick={() => setView('running')} className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 text-white text-sm font-black rounded-xl transition-all flex items-center justify-center gap-2 tracking-widest uppercase shadow-md sm:col-span-2">
                                    RUN CLUB & SCHEMA'S (1 - 42 KM) <Activity size={18}/>
                                </button>
                            </div>

                            <div className="w-full flex items-center gap-4 my-1.5 opacity-80">
                                <div className="h-px bg-zinc-600 flex-1"></div>
                                <span className="text-xs uppercase tracking-widest font-black text-gray-400">Of zelf bepalen</span>
                                <div className="h-px bg-zinc-600 flex-1"></div>
                            </div>

                            <div className="w-full flex flex-col sm:flex-row gap-3 relative z-10">
                                <button onClick={() => setView('builder')} className="flex-1 py-4 bg-zinc-800/80 hover:bg-zinc-700 text-gray-300 hover:text-white border border-zinc-600 hover:border-emerald-500 shadow-sm text-sm font-black rounded-xl transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-widest">
                                    <Wrench size={18} className="text-emerald-500" /> CUSTOM WORKOUT
                                </button>
                                <button onClick={() => setView('favorites')} className="flex-1 py-4 bg-zinc-800/80 hover:bg-zinc-700 text-gray-300 hover:text-white border border-zinc-600 hover:border-yellow-500 shadow-sm text-sm font-black rounded-xl transition-all duration-300 flex items-center justify-center gap-2 uppercase tracking-widest">
                                    <Star size={18} className="text-yellow-500" /> FAVORIETEN
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                    <div className="bg-zinc-800/40 p-4 sm:p-6 rounded-xl border border-zinc-700 flex flex-col justify-between h-full lg:max-h-[600px] relative">
                        <button onClick={() => getWod(null, true)} className="absolute top-4 left-4 flex items-center gap-1 text-gray-400 hover:text-white transition-colors bg-black/40 hover:bg-black/60 px-3 py-1.5 rounded-lg border border-zinc-700 text-sm font-bold z-20">
                            <ChevronLeft size={16} /> Terug
                        </button>

                        <div className="flex-1 overflow-y-auto pr-2 mt-10">
                            {(isRecovery || currentWod?.isDeload) && (
                                <div className="bg-zinc-800/50 border border-emerald-500/50 p-4 rounded-xl mb-4 text-emerald-500 flex items-center gap-2 text-sm font-bold">
                                    <Info size={18} className="shrink-0"/> Coach zegt: Deload & Herstel-sessie actief (65% intensiteit, verlaagd volume)!
                                </div>
                            )}

                            {currentWod?.avoidedMuscles && currentWod.avoidedMuscles.length > 0 && (
                                <div className="bg-zinc-800/30 border border-orange-500/40 p-3 rounded-xl mb-4 text-orange-500 flex items-center gap-2 text-xs sm:text-sm font-bold">
                                    🛡️ Aangepast schema: {currentWod.avoidedMuscles.map(k => AVOID_OPTIONS.find(o => o.key === k)?.label || k).join(', ')} ontzien vandaag.
                                </div>
                            )}

                            <div className="flex justify-between items-center mb-4">
                                <div className="flex items-center gap-3">
                                    <h3 className="text-xl sm:text-2xl font-black text-orange-500 uppercase tracking-wide">{displayTitle}</h3>
                                    <button
                                        onClick={handleSaveFavorite}
                                        disabled={isSavingFav}
                                        className={`p-1.5 rounded-full transition-all ${isSavingFav ? 'scale-125 text-yellow-500' : 'text-zinc-500 hover:text-yellow-500 hover:bg-yellow-500/10'}`}
                                        title="Opslaan als Favoriet"
                                    >
                                        <Star size={24} fill={isSavingFav ? "currentColor" : "none"} className={isSavingFav ? "animate-pulse" : ""} />
                                    </button>
                                </div>
                                <span className={`px-3 py-1 rounded text-sm font-bold ${currentWod.isDeload ? 'bg-emerald-600 text-white' : currentWod.isFitness ? 'bg-orange-900/30 text-orange-500 border border-orange-500/50' : 'bg-zinc-800 text-gray-300 border border-zinc-600'}`}>
                                    {currentWod.isDeload ? 'DELOAD 65%' : displayFormat}
                                </span>
                            </div>

                            {pastPerformanceText && (
                                <div className="bg-zinc-800/50 border border-cyan-500/40 p-3 rounded-lg mb-4 flex items-center gap-3 shadow-inner">
                                    <Award size={20} className="text-cyan-500 shrink-0" />
                                    <p className="text-sm font-bold text-gray-200">{pastPerformanceText}</p>
                                </div>
                            )}

                            {currentWod.isFitness && liveExercises.length > 0 ? (
                                <div className="space-y-4 mb-4 bg-black/40 p-4 rounded-xl border border-zinc-700">
                                    <h4 className="font-bold text-gray-400 uppercase tracking-widest text-xs mb-4 flex items-center gap-2"><Activity size={14}/> Workout Overzicht</h4>
                                    {['Warmup', 'Training', 'Cardio'].map(groupName => {
                                        const groupData = groups[groupName];
                                        if (!groupData || (Array.isArray(groupData) && groupData.length === 0) || Object.keys(groupData).length === 0) return null;

                                        if (groupName === 'Training') {
                                            return Object.entries(groupData).map(([letter, exArr]) => (
                                                <div key={letter} className="mb-4 border-l-2 border-orange-500/30 pl-3">
                                                   <p className="text-orange-500 font-bold text-sm mb-1 uppercase">Blok {letter} {exArr.length > 1 ? '(Superset)' : ''}</p>
                                                   {exArr.map(e => {
                                                        const strategyBadge = e.strategy === 'deload' ? <span className="ml-2 text-[10px] bg-emerald-600/20 text-emerald-500 border border-emerald-500/40 px-1.5 py-0.5 rounded font-bold" title="Deload 65%">🌿 Deload</span>
                                                                            : e.strategy === 'back_off' ? <span className="ml-2 text-[10px] bg-green-900/50 text-green-500 border border-green-700 px-1.5 py-0.5 rounded font-bold" title="Herstel Set">🟢 Back-off</span>
                                                                            : e.strategy === 'volume_push' ? <span className="ml-2 text-[10px] bg-orange-900/50 text-orange-500 border border-orange-700 px-1.5 py-0.5 rounded font-bold" title="Volume Push">🔥 Push</span>
                                                                            : null;
                                                        const weightDisplay = e.weight > 0 ? ` (${e.weight}${e.unit ? e.unit.toLowerCase() : 'kg'})` : '';

                                                        return (
                                                            <div key={e.originalIdx} className="text-gray-300 text-sm flex items-center justify-between gap-2 mb-2 group">
                                                                <div className="flex items-center gap-2 flex-wrap">
                                                                    <span className="text-orange-500 font-black text-xs">{e.displayCode}</span>
                                                                    <span>{e.sets}x{e.reps} {e.cleanName || e.name}{weightDisplay}</span>
                                                                    {strategyBadge}
                                                                    {isShreddingUser && e.tempo && <span className="bg-zinc-800 text-purple-400 border border-zinc-600 text-[10px] px-1.5 py-0.5 rounded font-bold">T: {e.tempo}</span>}
                                                                    {isShreddingUser && e.rpe && <span className="bg-zinc-800 text-red-400 border border-zinc-600 text-[10px] px-1.5 py-0.5 rounded font-bold">RPE: {e.rpe}</span>}
                                                                </div>
                                                                <button
                                                                    onClick={() => setSwapModal({isOpen: true, idx: e.originalIdx, oldName: e.cleanName || e.name})}
                                                                    className="text-gray-500 hover:text-green-500 p-1.5 rounded-full hover:bg-white/10 transition-colors"
                                                                    title="Verander Oefening"
                                                                >
                                                                    <RefreshCw size={14} />
                                                                </button>
                                                            </div>
                                                        );
                                                   })}
                                                </div>
                                            ));
                                        } else {
                                            return (
                                                <div key={groupName} className={`mb-3 border-l-2 pl-3 ${groupName === 'Warmup' ? 'border-yellow-500/30' : 'border-cyan-500/30'}`}>
                                                   <p className={`font-bold text-sm mb-1 ${groupName === 'Warmup' ? 'text-yellow-500' : 'text-cyan-500'}`}>{groupName}</p>
                                                   {groupData.map(e => <p key={e.originalIdx} className="text-gray-300 text-sm"><span className="text-gray-500">-</span> {e.cleanName || e.name}</p>)}
                                                </div>
                                            );
                                        }
                                    })}
                                </div>
                            ) : (
                                <pre className="whitespace-pre-wrap font-sans text-lg sm:text-xl leading-relaxed text-gray-200 mb-4 p-4 bg-black/40 rounded-lg border border-zinc-700">{currentWod.description}</pre>
                            )}
                        </div>

                        <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-zinc-700 shrink-0 mt-4">
                            <button onClick={async () => { await requestWakeLock(); setupTimerForWod(currentWod); setView('focus'); }} className="glow-fire flex-1 py-4 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-500 hover:to-red-500 text-white rounded-xl transition-all font-black text-lg flex items-center justify-center gap-2 uppercase tracking-widest">
                                <Play size={20} fill="currentColor"/> {currentWod.isFitness ? 'START TRAINING' : 'START FOCUS'}
                            </button>
                            {(!isShreddingUser || currentWod.type === 'Cardio' || !currentWod.isFitness) && (
                                <button onClick={() => getWod()} className="flex-1 py-4 bg-zinc-800 hover:bg-zinc-700 rounded-xl font-bold transition-all flex justify-center items-center gap-2 text-lg border border-zinc-600">
                                    <RefreshCw size={18} /> ANDERE WOD
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="bg-zinc-800/40 p-4 sm:p-6 rounded-xl border border-zinc-700 h-full max-h-[600px] overflow-y-auto">
                        <h3 className="text-xl font-black text-orange-500 uppercase tracking-wide mb-4 flex items-center gap-2"><Target size={20} /> Oefeningen & Uitleg</h3>

                        {currentWod.isFitness && (
                            <div className="bg-zinc-800/30 p-4 rounded-lg border border-orange-500/20 text-gray-300 text-sm space-y-2 mb-4">
                                <p className="font-bold text-orange-500 flex items-center gap-2"><Dumbbell size={16}/> Krachttraining Tips:</p>
                                <ul className="list-disc pl-5 space-y-1 text-gray-400">
                                    <li>Neem de voorgeschreven rust tussen de sets.</li>
                                    {isShreddingUser && <li>Let op de RPE (Rate of Perceived Exertion) - hoe zwaar de set moet feelen.</li>}
                                    {isShreddingUser && <li>Tempo is genoteerd als (Zakkend, Rust, Omhoog, Rust).</li>}
                                </ul>
                            </div>
                        )}

                        {currentExercisesDetails.length > 0 ? (
                            <div className="space-y-3">
                                {currentExercisesDetails.map((ex, i) => (
                                    <div key={i} className="bg-black/30 p-4 rounded-lg border border-zinc-700 hover:bg-black/50 transition-colors">
                                        <h4 className="font-bold text-gray-200 mb-1 flex items-center gap-2"><Info size={16} className="text-orange-500"/> {ex.name}</h4>
                                        <p className="text-sm text-gray-400 pl-6">{ex.desc}</p>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="bg-black/30 p-4 rounded-lg border border-zinc-700 text-gray-300 italic text-sm">
                                <p className="mb-2 text-center text-gray-500">Geen specifieke tips in onze database gevonden voor deze workout.</p>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
